import React, { useState, useEffect, useCallback } from 'react';
import { FaDice, FaUsers, FaCoins, FaChartLine } from 'react-icons/fa';
import { adminApiService } from '../../services/adminApiService';
import { useAppSelector } from '../../store/hooks';
import './AdminComponents.css';

interface IGamingSummaryData {
  totalGames: number;
  activeProviders: number;
  totalPlayers: number;
  totalRevenue: number;
  activeSessions: number;
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Helper functions for cache management
const getCachedData = (key: string) => {
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      if (now - timestamp < CACHE_DURATION) {
        console.log(`IGamingSummary: Using cached data for ${key}, age: ${Math.round((now - timestamp) / 1000)}s`);
        return data;
      } else {
        console.log(`IGamingSummary: Cache expired for ${key}, age: ${Math.round((now - timestamp) / 1000)}s`);
        sessionStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.error(`Failed to get cached data for ${key}:`, error);
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
    console.log(`IGamingSummary: Cached data for ${key}`);
  } catch (error) {
    console.error(`Failed to cache data for ${key}:`, error);
  }
};

const IGamingSummary: React.FC = () => {
  const [summary, setSummary] = useState<IGamingSummaryData | null>(() => {
    const cached = getCachedData('igaming-summary');
    return cached || null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const adminUser = useAppSelector(state => state.auth.adminUser);

  const hasPermission = adminUser?.permissions?.includes('VIEW_ADMIN_IGAMING') || false;

  const loadSummary = useCallback(async () => {
    if (!hasPermission) {
      setLoading(false);
      return;
    }

    // Check cache first
    const cached = getCachedData('igaming-summary');
    if (cached) {
      console.log('IGamingSummary: Using cached data, skipping API call');
      setSummary(cached);
      setLoading(false);
      return;
    }

    console.log('IGamingSummary: Making API call');
    setLoading(true);
    setError(null);

    try {
      // Fetch providers and games to calculate summary
      const token = localStorage.getItem('adminToken') || undefined;
      const [providersResponse, gamesResponse] = await Promise.all([
        adminApiService.getIGamingProviders(0, 100, token),
        adminApiService.getIGamingGames(0, 100, undefined, undefined, token)
      ]);

      if (providersResponse.success && gamesResponse.success) {
        const providers = providersResponse.data?.content || providersResponse.data || [];
        const games = gamesResponse.data?.content || gamesResponse.data || [];

        // Calculate summary data
        const activeProviders = Array.isArray(providers) 
          ? providers.filter((p: any) => p.enabled !== false).length 
          : 0;
        
        const totalGames = Array.isArray(games) ? games.length : 0;

        // Mock data for now - replace with actual API calls when available
        const summaryData: IGamingSummaryData = {
          totalGames,
          activeProviders,
          totalPlayers: 0, // TODO: Get from API
          totalRevenue: 0, // TODO: Get from API
          activeSessions: 0 // TODO: Get from API
        };

        setSummary(summaryData);
        
        // Cache the summary
        setCachedData('igaming-summary', summaryData);
      } else {
        throw new Error('Failed to load iGaming data');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load iGaming summary');
      console.error('iGaming summary error:', err);
    } finally {
      setLoading(false);
    }
  }, [hasPermission]); // Removed adminApi from dependencies - it's stable

  useEffect(() => {
    if (hasPermission) {
      loadSummary();
    } else {
      setLoading(false);
    }
  }, [hasPermission, loadSummary]);

  if (!hasPermission) {
    return null;
  }

  if (loading) {
    return (
      <div className="igaming-summary-card stat-card">
        <div className="stat-card-header">
          <h3>
            <FaDice style={{ color: '#e7038e' }} />
            <span>iGaming Summary</span>
          </h3>
        </div>
        <div className="stat-card-content">
          <div className="loading-spinner-small"></div>
          <p>Loading iGaming statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="igaming-summary-card stat-card">
        <div className="stat-card-header">
          <h3>
            <FaDice style={{ color: '#e7038e' }} />
            <span>iGaming Summary</span>
          </h3>
        </div>
        <div className="stat-card-content">
          <p className="error-text">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="igaming-summary-card stat-card">
      <div className="stat-card-header">
        <h3>
          <FaDice style={{ color: '#e7038e' }} />
          <span>iGaming Summary</span>
        </h3>
      </div>
      <div className="stat-card-content">
        <div className="summary-stats-grid">
          <div className="summary-stat-item">
            <div className="summary-stat-icon">
              <FaDice style={{ color: '#e7038e' }} />
            </div>
            <div className="summary-stat-details">
              <div className="summary-stat-label">Total Games</div>
              <div className="summary-stat-value">{summary?.totalGames || 0}</div>
            </div>
          </div>
          <div className="summary-stat-item">
            <div className="summary-stat-icon">
              <FaUsers style={{ color: '#086afb' }} />
            </div>
            <div className="summary-stat-details">
              <div className="summary-stat-label">Active Providers</div>
              <div className="summary-stat-value">{summary?.activeProviders || 0}</div>
            </div>
          </div>
          <div className="summary-stat-item">
            <div className="summary-stat-icon">
              <FaUsers style={{ color: '#0dca9d' }} />
            </div>
            <div className="summary-stat-details">
              <div className="summary-stat-label">Total Players</div>
              <div className="summary-stat-value">{summary?.totalPlayers || 0}</div>
            </div>
          </div>
          <div className="summary-stat-item">
            <div className="summary-stat-icon">
              <FaCoins style={{ color: '#fbb445' }} />
            </div>
            <div className="summary-stat-details">
              <div className="summary-stat-label">Total Revenue</div>
              <div className="summary-stat-value">${(summary?.totalRevenue || 0).toLocaleString()}</div>
            </div>
          </div>
          <div className="summary-stat-item">
            <div className="summary-stat-icon">
              <FaChartLine style={{ color: '#e7038e' }} />
            </div>
            <div className="summary-stat-details">
              <div className="summary-stat-label">Active Sessions</div>
              <div className="summary-stat-value">{summary?.activeSessions || 0}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IGamingSummary;

